import { Box, ButtonBase, Typography } from "@mui/material";
import { type DeviceCalibration } from "../analysis-calibration";
import { type AnalysisPreset, estimateAnalysisTimePerGame } from "../analysis-presets";
import { PRESET_ICON_OFFSET } from "../constants";
import { ACTIVE_SURFACE, DARK_SURFACES, layoutTokens } from "@/shared/theme";

type AnalysisPresetSelectorProps = {
  presets: AnalysisPreset[];
  preset: AnalysisPreset;
  calibrations?: Record<string, DeviceCalibration | undefined>;
  onSelect: (preset: AnalysisPreset) => void;
  disabled?: (preset: AnalysisPreset) => boolean;
  showHeader?: boolean;
};

export const AnalysisPresetSelector = ({
  presets,
  preset,
  calibrations = {},
  onSelect,
  disabled,
  showHeader = true,
}: AnalysisPresetSelectorProps) => (
  <Box data-tour="analysis-presets">
    {showHeader && (
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "baseline" },
          justifyContent: "space-between",
          gap: { xs: 0.65, sm: 2 },
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.12em" }}>
            01 / CHOOSE THE SEARCH
          </Typography>
          <Typography sx={{ mt: 0.4, fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.03em" }}>
            Analysis depth.
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", maxWidth: 220, textAlign: { xs: "left", sm: "right" } }}
        >
          Choose how far Nikola should calculate.
        </Typography>
      </Box>
    )}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
        gap: layoutTokens.controlGap,
      }}
    >
      {presets.map((item) => (
        <ButtonBase
          key={item.id}
          data-tour={`analysis-preset-${item.id}`}
          onClick={() => onSelect(item)}
          aria-pressed={preset.id === item.id}
          disabled={disabled?.(item)}
          disableRipple
          sx={{
            minWidth: 0,
            textAlign: "left",
            opacity: disabled?.(item) ? 0.4 : 1,
            "&:focus-visible > div": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: { xs: 168, sm: 204, md: 228 },
              boxSizing: "border-box",
              p: { xs: 2, sm: 2.5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "space-between",
              textAlign: "left",
              bgcolor: preset.id === item.id ? ACTIVE_SURFACE : "background.paper",
              border: "1px solid",
              borderColor: preset.id === item.id ? ACTIVE_SURFACE : "divider",
              color: preset.id === item.id ? DARK_SURFACES.canvas : "text.primary",
              transition: "none",
              "&:hover": {
                bgcolor: "primary.main",
                borderColor: "primary.main",
                color: "background.default",
                "& .analysis-preset-detail": { color: "rgba(10,10,10,0.68)" },
              },
            }}
          >
            <Typography
              sx={{
                alignSelf: "flex-start",
                fontSize: { xs: "3rem", sm: "3.9rem", md: "4.4rem" },
                lineHeight: 1,
                transform: `translateX(${PRESET_ICON_OFFSET[item.id] ?? "0"})`,
              }}
            >
              {item.icon}
            </Typography>
            <Box>
              <Typography
                sx={{ fontWeight: 800, fontSize: { xs: "1rem", sm: "1.1rem", md: "1.12rem" }, letterSpacing: "0.04em" }}
              >
                {item.label.toUpperCase()} · D{item.depth}
              </Typography>
              <Typography
                className="analysis-preset-detail"
                sx={{
                  mt: 0.5,
                  color: preset.id === item.id ? "rgba(10,10,10,0.68)" : "text.secondary",
                  fontSize: "0.68rem",
                  lineHeight: 1.4,
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                }}
              >
                {item.description}
              </Typography>
              <Typography
                className="analysis-preset-detail"
                variant="caption"
                sx={{
                  mt: 0.75,
                  display: "block",
                  color: preset.id === item.id ? "rgba(10,10,10,0.68)" : "text.secondary",
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                }}
              >
                ~ {estimateAnalysisTimePerGame(item, undefined, calibrations[item.id]?.typicalSeconds)} / GAME
              </Typography>
            </Box>
          </Box>
        </ButtonBase>
      ))}
    </Box>
  </Box>
);
